import { type FC, type ReactNode, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { useScrollCarouselItem } from 'dibs-scroll-carousel/exports/useScrollCarouselItem.ts';
import { useScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { getSrcSetStringXDescriptors } from 'dibs-image-utils/exports/srcSet.ts';

import Gallery from 'dibs-icons/exports/legacy/Gallery.ts';
import ArtworkFrame from 'dibs-icons/exports/legacy/ArtworkFrame.ts';
import PlayButtonInverse from 'dibs-icons/exports/legacy/PlayButtonInverse.ts';

import { trackThumbnailClick } from './tracking.ts';

import { type CarouselThumb_sortedPhoto$key } from './__generated__/CarouselThumb_sortedPhoto.graphql.ts';
import { type CarouselThumb_videoVimeo$key } from './__generated__/CarouselThumb_videoVimeo.graphql.ts';
import { type CarouselThumb_viewInRoomPhoto$key } from './__generated__/CarouselThumb_viewInRoomPhoto.graphql.ts';
import { type CarouselThumb_muxVideo$key } from './__generated__/CarouselThumb_muxVideo.graphql.ts';

import styles from './CarouselThumb.scss';

const sortedPhotoFragment = graphql`
    fragment CarouselThumb_sortedPhoto on ItemPhoto {
        masterOrZoomPath
        alt
    }
`;

const videoVimeoFragment = graphql`
    fragment CarouselThumb_videoVimeo on VideoVimeo {
        thumbnail(size: w100h75, includePlayButton: false, pad: false)
    }
`;

const muxVideoFragment = graphql`
    fragment CarouselThumb_muxVideo on VideoV2 {
        thumbnail(width: 100, height: 75)
    }
`;

const viewInRoomPhotoFragment = graphql`
    fragment CarouselThumb_viewInRoomPhoto on viewInRoomPhotos {
        src
        credits
    }
`;

type Size = 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';

export const CarouselThumb: FC<
    | {
          type: 'photo';
          index: number;
          size?: Size;
          sortedPhoto: CarouselThumb_sortedPhoto$key;
          videoVimeo: null;
          muxVideo: null;
          viewInRoomPhoto: null;
      }
    | {
          type: 'video';
          index: number;
          size?: Size;
          sortedPhoto: null;
          videoVimeo?: CarouselThumb_videoVimeo$key | null;
          muxVideo?: CarouselThumb_muxVideo$key | null;
          viewInRoomPhoto: null;
      }
    | {
          type: 'viewInRoom';
          index: number;
          size?: Size;
          sortedPhoto: null;
          videoVimeo: null;
          muxVideo: null;
          viewInRoomPhoto: CarouselThumb_viewInRoomPhoto$key;
      }
    | {
          type: 'requestMore';
          index: number;
          size?: Size;
          sortedPhoto: null;
          videoVimeo: null;
          muxVideo: null;
          viewInRoomPhoto: null;
      }
> = ({
    type,
    index,
    size = 'large',
    sortedPhoto: sortedPhotoRef,
    videoVimeo: videoVimeoRef,
    muxVideo: muxVideoRef,
    viewInRoomPhoto: viewInRoomPhotoRef,
}) => {
    const sortedPhoto = useFragment(sortedPhotoFragment, sortedPhotoRef);
    const videoVimeo = useFragment(videoVimeoFragment, videoVimeoRef);
    const muxVideo = useFragment(muxVideoFragment, muxVideoRef);
    const viewInRoomPhoto = useFragment(viewInRoomPhotoFragment, viewInRoomPhotoRef);

    const { isActive, setActive } = useScrollCarouselItem();
    const { totalSlides } = useScrollCarousel();

    const handleClick = useCallback(() => {
        trackThumbnailClick({
            activeSlide: index + 1,
            totalSlides,
            slideType: type,
        });
        setActive();
    }, [index, totalSlides, type, setActive]);

    let child: ReactNode = null;
    let className = '';

    if (type === 'photo' && sortedPhoto) {
        const { masterOrZoomPath, alt } = sortedPhoto;
        const srcSet = getSrcSetStringXDescriptors({ url: masterOrZoomPath });
        child =
            masterOrZoomPath && alt && srcSet ? (
                <img className={styles.image} src={masterOrZoomPath} alt={alt} srcSet={srcSet} />
            ) : null;
    } else if (type === 'video' && (videoVimeo || muxVideo)) {
        const src = videoVimeo?.thumbnail || muxVideo?.thumbnail;
        child = src ? (
            <>
                <img className={styles.image} src={src} alt="" />
                <div className={styles.videoThumbOverlay}>
                    <PlayButtonInverse className={styles.videoIcon} />
                </div>
            </>
        ) : null;
    } else if (type === 'viewInRoom' && viewInRoomPhoto) {
        const { src, credits } = viewInRoomPhoto;
        const srcSet = getSrcSetStringXDescriptors({ url: src });
        child =
            src && credits && srcSet ? (
                <>
                    <img className={styles.image} src={src} alt={credits} srcSet={srcSet} />
                    <div className={styles.viewInRoomOverlay}>
                        <ArtworkFrame className={styles.viewInRoomIcon} />
                    </div>
                </>
            ) : null;
    } else if (type === 'requestMore') {
        className = styles.requestMoreThumb;
        child = <Gallery className={styles.requestMoreIcon} />;
    }

    return (
        <Link
            className={classnames(styles.thumb, className, styles[size], {
                [styles.active]: isActive,
            })}
            onClick={handleClick}
            dataTn="carousel-thumb"
        >
            {child}
        </Link>
    );
};
